/**
 * El cuadro de eliminación directa, dibujado como se dibuja en papel: las dos
 * mitades enfrentadas y la final en el medio.
 *
 * POR QUÉ A DOS LADOS EN PANTALLA ANCHA. Un cuadro de 64 en una sola
 * dirección son seis columnas cada vez más altas: en un monitor la final
 * queda fuera de la vista y hay que desplazarse a lo ancho para saber quién
 * juega contra quién. Partido al medio entra en la mitad del ancho, y lo que
 * queda en el centro —la final— es justo lo que la gente busca primero.
 *
 * Y POR QUÉ NO EN UN TELÉFONO. Ahí se ven dos columnas por vez, así que la
 * forma de dos mitades no ahorra nada y encima cambia el sentido de lectura a
 * mitad de camino: se arrastra hacia la derecha, se llega a la final, y de
 * ahí en adelante el cuadro va para el otro lado. Angosto se dibuja en una
 * sola dirección. Ver `useAnchaLaPantalla`.
 *
 * SIN RECUADROS. El partido no es una tarjeta: es dos nombres subrayados,
 * unidos por la llave que los lleva a la ronda siguiente, con el marcador
 * escrito sobre esa llave. Es como se dibuja un cuadro en papel, y es lo que
 * deja leer de un vistazo quién sigue —los recuadros hacían que cada partido
 * pesara lo mismo que el cuadro entero.
 *
 * SE DIBUJA DESDE LOS PARTIDOS, SIEMPRE. Antes de tirar el torneo el servidor
 * los calcula sin guardarlos, y después los lee de la tabla; para esta
 * pantalla es lo mismo. Es lo que hace que el cuadro se vea igual antes y
 * después, y que no existan dos maneras de dibujarlo que se puedan
 * contradecir.
 *
 * LA ALINEACIÓN ES DE CSS Y NO DE JAVASCRIPT: cada columna es un flex con
 * `space-around`, así que cada partido queda centrado frente a los dos que lo
 * alimentan sin medir un solo píxel. Medir posiciones a mano es lo que hace
 * que un cuadro se descuadre al cambiar el tamaño de la letra.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DrawableMatch, MatchContact, RoundDeadline } from '@tennisup/contracts';

import { Avatar } from './Avatar';
import { WhatsAppIcon } from './icons';
import { assetUrl } from '../lib/api';
import { useAnchaLaPantalla } from '../lib/useAnchaLaPantalla';
import { whatsappHref } from '../lib/whatsapp';
import { nombreParaLeer } from '../lib/nombreParaLeer';
import { categoryName } from '../lib/categoryName';
import { formatDay } from '../lib/formatDay';

/**
 * De qué lado del cuadro está una columna, que es lo que decide de qué lado
 * se dibuja la llave y hacia dónde apunta la flecha del marcador.
 *
 * `undefined` es «esto no es un cuadro»: los partidos de grupo usan el mismo
 * dibujo de partido y no tienen ronda siguiente a la que apuntar.
 */
type Lado = 'left' | 'right' | 'final';

/** El `t` de i18next, tal como lo devuelve el hook: escribirlo a mano no compila. */
type Traducir = ReturnType<typeof useTranslation>['t'];

/**
 * GENÉRICO SOBRE EL PARTIDO, y no atado a `Match`.
 *
 * El cuadro se dibuja dos veces con datos distintos: adentro de la aplicación
 * con `Match` —que trae teléfonos y permisos— y en la vista pública con
 * `PublicMatch`, que trae el apodo y nada más. Los dos cumplen
 * `DrawableMatch`, que es exactamente lo que el dibujo necesita.
 *
 * La alternativa era que la vista pública fabricara `Match` a medio llenar
 * para conformar al compilador: un `userId` en blanco, un `canScore` en
 * falso. Eso es una promesa falsa —el tipo dice que hay un usuario— y es
 * cómo un campo privado termina viajando el día que alguien lo completa
 * «para que no quede vacío».
 */
interface BracketProps<M extends DrawableMatch> {
  /** Los partidos de UNA categoría, en cualquier orden. */
  matches: readonly M[];
  /** Para el mensaje de WhatsApp: «te escribo por el Abierto de Grecia». */
  tournamentName: string;
  /** Si se puede anotar. Solo con el torneo en juego y quien administra. */
  onScore?: (match: M) => void;
  /** Las fechas límite de ESTA categoría, ya filtradas. */
  deadlines?: readonly RoundDeadline[];
  /** Si se pueden cambiar. Solo quien administra. */
  onDeadline?: (players: number, date: string | null) => void;
  /** Si se puede programar un partido. Solo con el torneo en juego. */
  onSchedule?: (match: M) => void;
  /**
   * El id de inscripción a señalar en TODAS sus llaves. Ver `BracketSearch`.
   *
   * Señala, no filtra: un cuadro filtrado deja de ser un cuadro, porque lo
   * que se lee ahí es contra quién juega cada uno.
   */
  destacado?: string | null;
  /**
   * Tocar a alguien para moverlo de lugar. Solo en el BORRADOR, antes de
   * tirar: después el orden está fijo y hay resultados jugados contra él.
   *
   * Sin esto, cada hueco es un `div` y no un botón — ver `Slot`.
   */
  onPick?: (entryId: string) => void;
  /** A quién se tocó primero, esperando con quién cambiarlo. */
  elegido?: string | null;
}

/** Los partidos agrupados por ronda, de la primera a la final. */
export function byRound<M extends DrawableMatch>(matches: readonly M[]): M[][] {
  const rondas = matches.length === 0 ? 0 : Math.max(...matches.map((m) => m.round));
  const columnas: M[][] = [];
  for (let round = 1; round <= rondas; round += 1) {
    columnas.push(
      matches.filter((m) => m.round === round).sort((a, b) => a.position - b.position),
    );
  }
  return columnas;
}

/**
 * DESDE DÓNDE SE DIBUJA: la primera ronda que alguien puede jugar.
 *
 * Un cuadro de 32 con cuatro inscritos son dieciséis llaves de la primera
 * ronda, y catorce de ellas dicen «Bye» contra «Bye». Eso no es información:
 * es una pantalla de dos metros de alto que hay que recorrer entera para
 * encontrar los dos partidos que existen. La primera ronda con al menos un
 * partido de verdad —los dos rivales puestos— es donde empieza el torneo, y
 * es donde tiene que empezar el dibujo.
 *
 * PERO NO SE BORRA A NADIE, Y ESA ES LA CONDICIÓN QUE MANDA. Quien pasó sin
 * jugar aparece en la ronda que se saltearía, y el servidor ya lo dejó
 * colocado en la siguiente al armar el cuadro. Antes de recortar se comprueba
 * eso mismo: que cada persona que está en las rondas descartadas siga estando
 * más adelante. Si alguna se perdería, no se recorta nada y se dibuja el
 * cuadro entero — feo, pero completo. Un cuadro que se come a un jugador es
 * peor que un cuadro largo.
 */
export function desdeDondeSeJuega<M extends DrawableMatch>(columnas: M[][]): M[][] {
  const jugable = (m: DrawableMatch): boolean => m.home !== null && m.away !== null;
  let primera = columnas.findIndex((ronda) => ronda.some(jugable));
  // Ninguna ronda jugable: no hay nada que recortar con criterio.
  if (primera <= 0) return columnas;

  const quedan = new Set<string>();
  for (const ronda of columnas.slice(primera)) {
    for (const m of ronda) {
      if (m.home !== null) quedan.add(m.home.id);
      if (m.away !== null) quedan.add(m.away.id);
    }
  }

  for (const ronda of columnas.slice(0, primera)) {
    for (const m of ronda) {
      const suyos = [m.home?.id, m.away?.id].filter((id): id is string => id !== undefined);
      if (suyos.some((id) => !quedan.has(id))) return columnas;
    }
  }

  return columnas.slice(primera);
}

export function Bracket<M extends DrawableMatch>({
  matches,
  tournamentName,
  onScore,
  deadlines,
  onDeadline,
  onSchedule,
  destacado = null,
  onPick,
  elegido = null,
}: BracketProps<M>): JSX.Element | null {
  const { t } = useTranslation();
  const enDosMitades = useAnchaLaPantalla();

  const columnas = desdeDondeSeJuega(byRound(matches));
  if (columnas.length === 0) return null;

  const finalIndex = columnas.length - 1;
  const size = 2 ** columnas.length;

  const comunes = {
    tournamentName,
    onScore,
    deadlines,
    onDeadline,
    onSchedule,
    destacado,
    onPick,
    elegido,
  };

  // ANGOSTO: una sola dirección, de la primera ronda a la final. Una columna
  // por ronda, enteras, en el orden en que se juegan.
  if (!enDosMitades) {
    return (
      <div className="bracket-scroll">
        <div className="bracket bracket-una-direccion">
          {columnas.map((ms, i) => (
            <Column
              key={`u${i}`}
              title={roundTitle(t, size, i)}
              players={size / 2 ** i}
              round={i + 1}
              matches={ms}
              // La final también apunta a la derecha acá: no es el centro de
              // nada, es la última columna. Lo que corta la flecha es que el
              // campeón no tiene ronda siguiente, y de eso se encarga `Lado`.
              side={i === finalIndex ? 'final' : 'left'}
              {...comunes}
            />
          ))}
        </div>

        <p className="help bracket-legend">{t('bracket.seedLegend')}</p>
      </div>
    );
  }

  // ANCHO: las rondas previas a la final se parten al medio: la mitad de
  // arriba va a la izquierda y la de abajo a la derecha, que es como se lee
  // un cuadro de papel.
  const izquierda = columnas.slice(0, finalIndex).map((ms) => ms.slice(0, ms.length / 2));
  const derecha = columnas
    .slice(0, finalIndex)
    .map((ms) => ms.slice(ms.length / 2))
    // La derecha se dibuja de afuera hacia adentro: sus columnas van al revés.
    .reverse();
  const laFinal = columnas[finalIndex] ?? [];

  return (
    <div className="bracket-scroll">
      <div className="bracket">
        {izquierda.map((ms, i) => (
          <Column
            key={`l${i}`}
            title={roundTitle(t, size, i)}
            players={size / 2 ** i}
            round={i + 1}
            matches={ms}
            side="left"
            {...comunes}
          />
        ))}

        <Column
          title={t('bracket.final')}
          players={2}
          round={columnas.length}
          matches={laFinal}
          side="final"
          {...comunes}
        />

        {derecha.map((ms, i) => (
          <Column
            key={`r${i}`}
            title={roundTitle(t, size, derecha.length - 1 - i)}
            players={size / 2 ** (derecha.length - 1 - i)}
            round={derecha.length - i}
            matches={ms}
            side="right"
            {...comunes}
          />
        ))}
      </div>

      <p className="help bracket-legend">{t('bracket.seedLegend')}</p>
    </div>
  );
}

/** «Octavos», «Cuartos», «Semifinal»… según cuánta gente queda en esa ronda. */
function roundTitle(t: Traducir, size: number, roundIndex: number): string {
  const jugadores = size / 2 ** roundIndex;
  if (jugadores === 2) return t('bracket.final');
  if (jugadores === 4) return t('bracket.semi');
  if (jugadores === 8) return t('bracket.quarter');
  return t('bracket.roundOf', { n: jugadores });
}

function Column<M extends DrawableMatch>({
  title,
  players,
  round,
  matches,
  tournamentName,
  side,
  onScore,
  deadlines,
  onDeadline,
  onSchedule,
  destacado,
  onPick,
  elegido,
}: {
  title: string;
  /** Cuánta gente queda al empezar esta ronda: es la llave de la fecha límite. */
  players: number;
  /** El número de ronda, 1 la primera. Solo sirve para saber si un hueco es un BYE. */
  round: number;
  matches: readonly M[];
  tournamentName: string;
  side: Lado;
  // `| undefined` explícito: con `exactOptionalPropertyTypes`, pasar
  // `onScore={undefined}` no es lo mismo que no pasar la prop, y acá se pasa
  // siempre porque viene de arriba.
  onScore: ((match: M) => void) | undefined;
  deadlines: readonly RoundDeadline[] | undefined;
  onDeadline: ((players: number, date: string | null) => void) | undefined;
  onSchedule: ((match: M) => void) | undefined;
  destacado: string | null;
  onPick: ((entryId: string) => void) | undefined;
  elegido: string | null;
}): JSX.Element {
  return (
    <div className={`bracket-column bracket-${side}`}>
      {/* El encabezado va arriba Y abajo: en un cuadro alto, la columna de
          más abajo queda a media pantalla del título y nadie se acuerda de
          qué ronda estaba mirando. */}
      <p className="bracket-round-name">{title}</p>
      <RoundDeadlineLine players={players} deadlines={deadlines} onDeadline={onDeadline} />
      <div className="bracket-matches">
        {matches.map((match) => (
          <MatchBox
            key={match.id}
            match={match}
            tournamentName={tournamentName}
            side={side}
            // Cuántos quedan en esta ronda: es de lo que sale su nombre
            // —«Cuartos» son ocho— y con eso se nombra el botón de la fecha
            // cuando todavía no se sabe quiénes juegan.
            players={players}
            primeraRonda={round === 1}
            destacado={destacado}
            onPick={onPick}
            elegido={elegido}
            onScore={onScore}
            onSchedule={onSchedule}
          />
        ))}
      </div>
      <p className="bracket-round-name">{title}</p>
    </div>
  );
}

/**
 * LA FECHA LÍMITE DE LA RONDA, debajo de su título.
 *
 * Ahí y no en una pantalla de configuración aparte: la pregunta «¿hasta
 * cuándo hay para jugar los cuartos?» se hace mirando los cuartos.
 *
 * Quien no organiza la ve como texto; quien organiza ve el calendario. Es el
 * mismo lugar para las dos cosas, así que nadie tiene que buscar dónde se
 * cambia.
 */
function RoundDeadlineLine({
  players,
  deadlines,
  onDeadline,
}: {
  players: number;
  deadlines: readonly RoundDeadline[] | undefined;
  onDeadline: ((players: number, date: string | null) => void) | undefined;
}): JSX.Element | null {
  const { t } = useTranslation();
  const propia = deadlines?.find((d) => d.round === players)?.deadline ?? '';
  const [abierto, setAbierto] = useState(false);

  if (onDeadline === undefined) {
    if (propia === '') return null;
    return <p className="help bracket-deadline">{t('schedule.playBy', { date: formatDay(propia) })}</p>;
  }

  /**
   * EL CALENDARIO NO ESTÁ PUESTO DE ENTRADA.
   *
   * La fecha límite es opcional y la mayoría de los torneos no la usa: un
   * campo vacío encima de CADA ronda —hasta seis en un cuadro de 64— llena la
   * pantalla de casillas que nadie va a llenar, y encima empuja el cuadro
   * hacia abajo, que es lo que se vino a mirar.
   *
   * Con fecha puesta se muestra directo, porque ahí sí hay algo que decir.
   */
  if (!abierto && propia === '') {
    return (
      <p className="bracket-deadline">
        <button
          type="button"
          className="link"
          /*
            DE QUÉ RONDA. Hay uno por columna —hasta seis en un cuadro de 64—
            y todos decían «+ fecha límite». La fecha límite le dice a la
            gente hasta cuándo tiene para jugar: ponérsela a la ronda
            equivocada apura a media categoría sin motivo, o deja pasar la que
            de verdad vencía.
          */
          aria-label={t('schedule.addPlayByOf', { round: roundTitle(t, players, 0) })}
          onClick={() => setAbierto(true)}
        >
          {t('schedule.addPlayBy')}
        </button>
      </p>
    );
  }

  return (
    <p className="bracket-deadline">
      <label className="help" htmlFor={`deadline-${players}`}>
        {t('schedule.playByLabel')}
      </label>
      <input
        id={`deadline-${players}`}
        type="date"
        value={propia}
        onChange={(e) => onDeadline(players, e.target.value === '' ? null : e.target.value)}
      />
    </p>
  );
}

/**
 * Un partido dibujado. Se exporta porque las TABLAS lo usan igual: un partido
 * de grupo se ve —y se anota, y se programa— exactamente como uno de llaves,
 * y dibujarlo dos veces sería tener dos partidos distintos según dónde caiga.
 */
export function MatchBox<M extends DrawableMatch>({
  match,
  tournamentName,
  side,
  primeraRonda = false,
  destacado = null,
  onPick,
  elegido = null,
  players,
  onScore,
  onSchedule,
}: {
  match: M;
  tournamentName: string;
  /**
   * De qué lado del cuadro cae. Sin esto —los partidos de grupo— no se dibuja
   * la llave ni la flecha: un partido de grupo no lleva a ninguna ronda.
   */
  side?: Lado;
  /**
   * UN HUECO EN LA PRIMERA RONDA ES UN BYE; en cualquier otra es «todavía no
   * se sabe quién llega». Se ven igual —un lugar vacío— y no son lo mismo:
   * uno ya está resuelto para siempre y el otro se resuelve el sábado.
   */
  primeraRonda?: boolean;
  /** El id de inscripción señalado, si hay uno. Ver `BracketSearch`. */
  destacado?: string | null;
  onPick?: ((entryId: string) => void) | undefined;
  elegido?: string | null;
  /**
   * Cuántos jugadores quedan en esta ronda. Da su nombre: ocho son «Cuartos».
   *
   * OPCIONAL porque hay un `MatchBox` fuera del cuadro: el de «próximos
   * partidos» en la pantalla de inicio, donde no hay columna que lo sepa.
   * Ahí no hace falta — esos partidos siempre tienen a los dos rivales
   * puestos, así que el botón se nombra por ellos.
   */
  players?: number | undefined;
  onScore: ((match: M) => void) | undefined;
  onSchedule: ((match: M) => void) | undefined;
}): JSX.Element {
  const { t } = useTranslation();
  const jugado = match.winnerEntryId !== null;
  /**
   * QUIÉN PUEDE ANOTAR LO DICE EL SERVIDOR, en `match.canScore`.
   *
   * Antes lo decía la pantalla —«soy quien organiza y el torneo está en
   * juego»— y alcanzaba mientras anotar fuera cosa de una sola persona. Ya no
   * lo es: si el club lo permite, cada jugador anota EL PARTIDO QUE JUGÓ y
   * ninguno más, y eso depende de quién mira ESTE partido. La pantalla no
   * tiene esa respuesta; el servidor sí, y es el que la tiene que dar porque
   * es el que la aplica.
   *
   * Lo de los dos rivales se comprueba igual acá, y no es duplicar el
   * permiso: no es una pregunta de quién puede, es que un partido sin los dos
   * NO TIENE marcador que anotar —sería alguien contra nadie— y eso se ve
   * mirando el partido, sin preguntarle a nadie.
   */
  const anotable =
    onScore !== undefined &&
    // `=== true` y no a secas: en la vista pública `canScore` NO VIAJA, así
    // que llega `undefined`. Un `undefined` no es «puede», es «no hay quien».
    match.canScore === true &&
    match.home !== null &&
    match.away !== null;

  /**
   * EL MARCADOR VA ENTRE LOS DOS, SOBRE LA LLAVE, y no repartido en la fila
   * de cada quien.
   *
   * Antes cada jugador llevaba sus propios números —`6 6` arriba, `1 3`
   * abajo— y para leer el resultado había que juntar dos columnas con el ojo.
   * Escrito entero en el medio se lee como se dice: «6-1, 6-3». Además libera
   * el borde de la fila, que es donde ahora va la marca de quién pasó.
   */
  const contenido = (
    <>
      <Slot
        entry={match.home}
        bye={primeraRonda}
        gano={jugado && match.winnerEntryId === match.home?.id}
        perdio={jugado && match.home !== null && match.winnerEntryId !== match.home.id}
        noVino={match.noShowEntryId !== null && match.noShowEntryId === match.home?.id}
        señalado={destacado !== null && destacado === match.home?.id}
        onPick={onPick}
        elegido={elegido}
      />
      <Resultado match={match} side={side} />
      <Slot
        entry={match.away}
        bye={primeraRonda}
        gano={jugado && match.winnerEntryId === match.away?.id}
        perdio={jugado && match.away !== null && match.winnerEntryId !== match.away.id}
        noVino={match.noShowEntryId !== null && match.noShowEntryId === match.away?.id}
        señalado={destacado !== null && destacado === match.away?.id}
        onPick={onPick}
        elegido={elegido}
      />
    </>
  );

  const clase = `bracket-match${side === undefined ? '' : ` bracket-match-${side}`}`;

  /**
   * LA CAJA DEL PARTIDO NO ES UN SOLO BOTÓN CUANDO TAMBIÉN SE PROGRAMA.
   *
   * Anotar el marcador y poner la fecha son dos acciones distintas sobre el
   * mismo partido, y un botón que hace una cosa no puede hacer dos. El
   * marcador se sigue tocando en la caja —es lo que más se hace— y la fecha
   * tiene su propio botón chico debajo.
   */
  const cuando = <ScheduleLine schedule={match.schedule} />;

  const caja = !anotable ? (
    <div className={clase}>{contenido}</div>
  ) : (
    <button
      type="button"
      className={`${clase} bracket-match-editable`}
      onClick={() => onScore(match)}
      aria-label={
        jugado
          ? t('score.editOf', { home: match.home?.displayName, away: match.away?.displayName })
          : t('score.addOf', { home: match.home?.displayName, away: match.away?.displayName })
      }
    >
      {contenido}
    </button>
  );

  const contactos = <Contacts match={match} tournamentName={tournamentName} />;

  /**
   * LA FOTO DEL PARTIDO, DEBAJO Y CHIQUITA.
   *
   * No va adentro de la caja: la caja es un botón cuando se puede anotar, y
   * meterle una foto adentro hace que tocar la foto abra el marcador. Acá
   * abajo es una imagen y nada más — se ve, y quien quiera mirarla en grande
   * la abre desde el navegador.
   */
  /**
   * SIN FOTO NO SE DIBUJA NADA, y «sin foto» es más que `null`.
   *
   * Se le pregunta a `assetUrl`, que es quien sabe qué rutas no sirven: nula,
   * vacía, o ausente porque la API todavía no manda el campo. Preguntarlo acá
   * a mano —`=== null`— fue justamente el error: contra una API sin el campo
   * llegaba `undefined`, la URL quedaba en `/uploads/undefined`, y se dibujaba
   * el ícono de imagen rota con el texto alternativo al lado. De todo lo que
   * se puede dibujar con un dato en blanco, una imagen es lo único que se ve
   * ROTA en vez de simplemente no verse.
   */
  const urlDeLaFoto = assetUrl(match.photoPath);
  const foto =
    urlDeLaFoto === null ? null : (
      <img
        className="bracket-photo"
        src={urlDeLaFoto}
        alt={t('score.photoOf', {
          home: match.home?.displayName ?? '',
          away: match.away?.displayName ?? '',
        })}
        loading="lazy"
      />
    );

  if (onSchedule === undefined) {
    return (
      <div className="bracket-match-wrap" data-match={match.id}>
        {caja}
        {cuando}
        {contactos}
        {foto}
      </div>
    );
  }

  return (
    <div className="bracket-match-wrap" data-match={match.id}>
      {caja}
      {cuando}
      {contactos}
      {foto}
      <button
        type="button"
        className="link bracket-when"
        /*
          DE QUÉ PARTIDO. Hay uno por llave —dieciséis en un cuadro de 32— y
          todos decían lo mismo. Se nombra por quiénes juegan, que es como se
          habla de un partido: «el de Beto contra Ana».
        */
        aria-label={
          /*
            SIN LOS DOS RIVALES PUESTOS se nombra por la ronda y el número:
            en una ronda que todavía no se llenó no hay contra quién, pero
            sigue habiendo un botón por llave y todos decían «Poner fecha».
          */
          match.home === null || match.away === null
            ? players === undefined
              ? undefined
              : t('schedule.setOfSlot', {
                  round: roundTitle(t, players, 0),
                  n: match.position,
                })
            : t(
                match.schedule.date === null && match.schedule.place === null
                  ? 'schedule.setOf'
                  : 'schedule.editOf',
                {
                  home: nombreParaLeer(match.home),
                  away: nombreParaLeer(match.away),
                },
              )
        }
        onClick={() => onSchedule(match)}
      >
        {match.schedule.date === null && match.schedule.place === null
          ? t('schedule.set')
          : t('schedule.edit')}
      </button>
    </div>
  );
}

/**
 * A QUIÉN ESCRIBIRLE PARA ACORDAR ESTE PARTIDO.
 *
 * Un torneo de club se arma por WhatsApp: «¿te sirve el jueves a las 7?».
 * Antes había que saber quién era, buscarlo en el grupo del club y esperar
 * que alguien pasara el número.
 *
 * VA DEBAJO DE LA CAJA Y NO SOBRE EL NOMBRE. La caja entera es el botón de
 * anotar el marcador —es lo que más se hace con un partido— y un enlace
 * adentro de un botón no es HTML válido: el navegador decide cuál de los dos
 * gana y no siempre decide igual. Acá los dos gestos son distintos y ninguno
 * se come al otro.
 *
 * QUIÉN SALE ACÁ LO DECIDE EL SERVIDOR: la lista viene vacía cuando quien
 * mira no juega este partido ni organiza el torneo. La pantalla no esconde
 * un número que recibió; no lo recibe.
 */
function Contacts({
  match,
  tournamentName,
}: {
  match: DrawableMatch;
  tournamentName: string;
}): JSX.Element | null {
  const { t } = useTranslation();
  // Ausente —la vista pública— es lo mismo que vacía: no hay a quién escribirle.
  const contactos = match.contacts ?? [];
  if (contactos.length === 0) return null;

  const category = categoryName(t, match);

  const enlaces = contactos
    .map((contact) => ({
      contact,
      href: whatsappHref(
        contact.whatsappPhone,
        t('schedule.whatsappText', {
          name: contact.displayName,
          category,
          tournament: tournamentName,
        }),
      ),
    }))
    .filter((x): x is { contact: MatchContact; href: string } => x.href !== null);

  if (enlaces.length === 0) return null;

  return (
    <p className="bracket-contacts">
      {enlaces.map(({ contact, href }) => (
        <a
          key={contact.entryId}
          className="link"
          href={href}
          // Se abre aparte: quien está mirando el cuadro no quiere perderlo
          // para escribir un mensaje. `noreferrer` para no contarle a
          // WhatsApp de dónde vino.
          target="_blank"
          rel="noreferrer"
          aria-label={t('schedule.whatsappTo', { name: contact.displayName })}
        >
          {/* «WhatsApp» es el nombre de la aplicación: no se traduce. Lo que
              se lee en voz alta es el `aria-label` del enlace, que sí. */}
          <WhatsAppIcon title="WhatsApp" size={16} />
          <span>{contact.displayName}</span>
        </a>
      ))}
    </p>
  );
}

/**
 * Cuándo y dónde, en una línea.
 *
 * En el cuadro hay lugar para una línea y no más: el cuadro es para ver quién
 * juega contra quién. Lo que se muestra es lo que alguien necesita para
 * llegar —día, hora, cancha— y la descripción larga queda en el editor.
 */
function ScheduleLine({ schedule }: { schedule: DrawableMatch['schedule'] }): JSX.Element | null {
  const partes = [
    schedule.date === null ? null : formatDay(schedule.date),
    schedule.time,
    schedule.court ?? schedule.place,
  ].filter((x): x is string => x !== null && x !== '');

  if (partes.length === 0) return null;
  return <p className="help bracket-when">{partes.join(' · ')}</p>;
}

/**
 * EL MARCADOR SOBRE LA LLAVE, entre los dos jugadores.
 *
 * TRES COSAS DISTINTAS SEGÚN EN QUÉ ESTÁ EL PARTIDO, y las tres ocupan el
 * mismo renglón para que el cuadro no cambie de alto al anotarse un
 * resultado:
 *
 *  · Jugado con sets:  «6-1, 6-3 →»
 *  · Jugado sin sets:  «W.O. →» — alguien no llegó, no hubo qué anotar.
 *  · Sin jugar:        «vs» — hay dos y todavía no se jugó.
 *
 * LA FLECHA APUNTA A LA RONDA SIGUIENTE, no al ganador: en la mitad derecha
 * la ronda siguiente está a la IZQUIERDA, y por eso ahí la flecha se da
 * vuelta. Es lo que hace que el cuadro se lea hacia el centro sin tener que
 * pensarlo. En la final no hay flecha porque no hay a dónde ir, y en un
 * partido de grupo tampoco porque no hay cuadro.
 *
 * EL MARCADOR NO SE DA VUELTA EN LA MITAD DERECHA. «6-1» es de quien está
 * arriba, mire uno de donde mire; darlo vuelta según el lado es exactamente
 * cómo alguien copia mal el resultado al anotar la ronda siguiente.
 */
function Resultado({
  match,
  side,
}: {
  match: DrawableMatch;
  side: Lado | undefined;
}): JSX.Element | null {
  const { t } = useTranslation();

  // Sin los dos rivales no hay nada que decir: es un BYE o un lugar que
  // todavía no se llenó, y eso ya lo dice la fila de al lado.
  if (match.home === null || match.away === null) return null;

  const jugado = match.winnerEntryId !== null;
  const flecha =
    side === 'left' ? '→' : side === 'right' ? '←' : null;

  const texto = !jugado
    ? t('bracket.versus')
    : match.score.length === 0
      ? t('score.wo')
      : match.score.map((set) => `${set.home}-${set.away}`).join(', ');

  return (
    <p className={`bracket-result${jugado ? ' bracket-result-done' : ''}`}>
      {flecha === '←' && (
        <span className="bracket-arrow" aria-hidden="true">
          {flecha}
        </span>
      )}
      <span>{texto}</span>
      {flecha === '→' && (
        <span className="bracket-arrow" aria-hidden="true">
          {flecha}
        </span>
      )}
    </p>
  );
}

function Slot({
  entry,
  bye,
  gano,
  perdio,
  noVino,
  señalado,
  onPick,
  elegido = null,
}: {
  entry: DrawableMatch['home'];
  /** Si un lugar vacío en esta ronda es un BYE y no un «falta saber quién». */
  bye: boolean;
  gano: boolean;
  perdio: boolean;
  /** Esta persona no se presentó: el partido lo ganó la otra sin jugarse. */
  noVino: boolean;
  /** Es a quien se buscó: se señala en TODAS sus llaves. */
  señalado: boolean;
  /** Tocar para moverlo. Solo en el borrador; sin esto el hueco no es botón. */
  onPick?: ((entryId: string) => void) | undefined;
  /** A quién se tocó primero. */
  elegido?: string | null;
}): JSX.Element {
  const { t } = useTranslation();

  if (entry === null) {
    /**
     * UN LUGAR VACÍO SE DICE, NO SE DEJA EN BLANCO.
     *
     * En la primera ronda es un BYE: nadie va a aparecer ahí nunca, y el de
     * al lado ya pasó. En cualquier otra ronda es el lugar de quien gane el
     * partido anterior, y ahí sí va a aparecer alguien. Una raya tenue para
     * los dos casos era la forma más segura de que alguien creyera que al
     * cuadro le faltaba cargar.
     */
    if (bye) {
      return (
        <div className="bracket-slot bracket-slot-bye">
          <span className="bracket-bye">{t('bracket.bye')}</span>
        </div>
      );
    }
    return (
      <div className="bracket-slot bracket-slot-pending">
        <span className="bracket-name bracket-pending" aria-hidden="true" />
      </div>
    );
  }

  const clase = `bracket-slot${gano ? ' bracket-slot-won' : ''}${perdio ? ' bracket-slot-lost' : ''}${señalado ? ' bracket-slot-found' : ''}`;

  const adentro = (
    <>
      <span className="bracket-seed">{entry.seed}</span>
      {/* La foto es chica y decorativa: el nombre está al lado. Un cuadro de
          64 con 64 fotos grandes no entra en ninguna pantalla, y quien no ve
          la imagen tiene el apodo igual. */}
      <Avatar url={assetUrl(entry.avatarPath)} name={entry.displayName} size={22} />
      <span className="bracket-name">
        <span className="bracket-nick">{entry.displayName}</span>
        {/* El primer nombre debajo, en gris: en un cuadro hay tres «Beto» y
            el apodo solo no alcanza para saber cuál es cuál. */}
        {/* Puede faltar del todo: en la vista pública el nombre legal no
            viaja. Ausente y vacío son lo mismo acá — no hay qué escribir. */}
        {entry.firstName !== undefined && entry.firstName !== '' && (
          <span className="bracket-first">{entry.firstName}</span>
        )}
      </span>
      {/*
        W.O. EN LA FILA DE QUIEN NO LLEGÓ. El renglón del medio dice que hubo
        walkover; esta marca dice de quién fue la ausencia, que es la mitad
        que falta. Es como se lee un cuadro de papel: el «W.O.» está del lado
        que faltó.
      */}
      {noVino && (
        <span className="bracket-wo" title={t('score.noShowTitle')}>
          {t('score.wo')}
        </span>
      )}
      {/*
        QUIÉN PASÓ Y QUIÉN QUEDÓ, con dos marcas y no con una.
        
        Con solo el tilde del ganador, un partido sin jugar y un partido
        perdido se ven igual: ninguna marca. La cruz dice «este partido ya se
        jugó y este no sigue», que es información distinta de «todavía no».
      */}
      {gano && (
        <span className="bracket-mark bracket-mark-won" aria-label={t('score.winner')}>
          <span aria-hidden="true">✓</span>
        </span>
      )}
      {perdio && (
        <span className="bracket-mark bracket-mark-lost" aria-label={t('score.loser')}>
          <span aria-hidden="true">✕</span>
        </span>
      )}
    </>
  );

  /**
   * EN EL BORRADOR CADA HUECO ES UN BOTÓN; en el cuadro tirado, no.
   *
   * Tocar a alguien lo marca; tocar a otro los intercambia. Es lo que Javier
   * eligió sobre arrastrar, y en un cuadro de 32 que no entra en la pantalla
   * es lo único que no se pelea con el scroll: las dos puntas pueden estar a
   * mil píxeles de distancia.
   *
   * SIN `onPick` VUELVE A SER UN `div`. Un botón que no hace nada igual se
   * anuncia como botón, entra en la navegación por tabulador y cambia el
   * cursor: el cuadro ya tirado, que es el que mira todo el mundo, no tiene
   * por qué tener sesenta y cuatro botones muertos.
   */
  if (onPick === undefined) {
    return <div className={clase}>{adentro}</div>;
  }

  const esElegido = elegido === entry.id;
  return (
    <button
      type="button"
      className={`${clase} bracket-slot-pick${esElegido ? ' bracket-slot-picked' : ''}`}
      /*
       * EL NOMBRE DICE QUÉ HACE, no solo de quién es.
       *
       * «Beto, botón» no le dice a nadie para qué está ahí, y en esta
       * pantalla hay otros botones que también se llaman por el nombre del
       * jugador —la caja del partido, y el asa de la lista de siembra, que
       * decía exactamente «Mover a Beto»—. «Cambiar de lugar a Beto» dice
       * qué hace ESTE y no se confunde con ninguno.
       *
       * `aria-pressed` además dice si ESTE quedó marcado esperando con quién
       * cambiarse, que es un estado que la clase sola no comunica.
       */
      /*
       * CON EL NOMBRE DE PILA CUANDO LO HAY. El apodo se repite —el cuadro
       * muestra el nombre debajo justamente por eso— y sin él dos personas
       * distintas tienen el mismo nombre accesible.
       *
       * Lo arma `nombreParaLeer`, que es el mismo que usan la nómina y la
       * lista de siembra: siete botones que hablan de la misma persona tienen
       * que nombrarla igual, y una copia por componente es seis copias que se
       * van separando.
       */
      aria-label={t('draw.swap', { name: nombreParaLeer(entry) })}
      aria-pressed={esElegido}
      onClick={() => onPick(entry.id)}
    >
      {adentro}
    </button>
  );
}
